/**
 * The task side panel — prototype `openTask()` (docs/prototype.html, drawer "Task"). Hosted once in the
 * app frame and opened by `?task=<id>` on any page, because every Work row and every Today exception
 * routes to `/work/allwork?task=<id>` (contract `route`).
 *
 * Which buttons show comes from capabilities and the task itself (who it is with), never from a role:
 * the server refuses anything else anyway (404 out of scope, 403 "Ask for a change instead").
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  CHANGE_REASON_LABEL,
  HEAD_PRIORITY_LABEL,
  SLIP_REASON_LABEL,
  TASK_SOURCE_LABEL,
  type AuditEntry,
  type Closure,
  type Task,
  type TaskPriority,
} from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { dayLabel, firstName, todayIso } from '../../lib/format';
import { useSession } from '../../lib/session';
import { daysBetween, dueText, useTask, useTaskAudit } from '../../lib/work';
import { Drawer, Empty, ErrorState, Loading } from '../../ui/kit';
import {
  AskChangeModal,
  BlockModal,
  CloseWithProofModal,
  DecideChangeModal,
  DropTaskModal,
  MoveDueModal,
  ReassignModal,
  ReopenModal,
} from './TaskModals';

const PRI: Record<TaskPriority, { label: string; cls: string }> = {
  urgent: { label: 'Urgent', cls: 'cold' },
  high: { label: 'High', cls: 'warm' },
  medium: { label: 'Medium', cls: 'info' },
  low: { label: 'Low', cls: '' },
};

/** Prototype `priChip()`. */
export const PriorityTaskChip = ({ p }: { p: TaskPriority }) => <span className={`chip ${PRI[p].cls}`}>{PRI[p].label}</span>;

/** Prototype `slipChip()`: "Slipped 5d, 2 pushes" — cold from 3 pushes or a week late. */
export function SlipChip({ task }: { task: Pick<Task, 'slip_count' | 'orig_due' | 'due' | 'done'> }) {
  if (!task.slip_count || task.done || !task.orig_due) return null;
  const d = daysBetween(task.orig_due, task.due);
  const n = task.slip_count;
  return (
    <span className={`chip ${n >= 3 || d >= 7 ? 'cold' : 'warm'}`} title={`First due ${dayLabel(task.orig_due)}`}>
      Slipped {d}d{n > 1 ? `, ${n} pushes` : ''}
    </span>
  );
}

/** Prototype `proofBlock()`: what was handed in, and whether it has been checked. */
export function ProofBlock({ closure }: { closure: Closure }) {
  const by = closure.closed_by_on_behalf_of
    ? `${firstName(closure.closed_by.name)}, acting as ${firstName(closure.closed_by_on_behalf_of.name)}`
    : firstName(closure.closed_by.name);
  return (
    <div className="note" style={{ margin: 0, display: 'grid', gap: 6 }}>
      {closure.proof_url ? (
        <a href={closure.proof_url} target="_blank" rel="noreferrer" className="chip info" style={{ justifySelf: 'start', textDecoration: 'none', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {closure.proof_url.replace(/^https?:\/\//, '').slice(0, 60)}
        </a>
      ) : closure.no_attachment_needed ? (
        <span className="sub">No attachment needed</span>
      ) : null}
      {closure.proof_text ? <div>{closure.proof_text}</div> : null}
      {closure.next_step ? (
        <div>
          <b>Next step:</b> {closure.next_step}
        </div>
      ) : null}
      <div className="split" style={{ flexWrap: 'wrap' }}>
        <span className="sub">
          Closed by {by}, {dayLabel(closure.closed_at.slice(0, 10))}
        </span>
        <div className="grow" />
        {closure.check_state === 'checked' ? (
          <span className="chip hot">Checked by {closure.checked_by ? firstName(closure.checked_by.name) : 'a manager'}</span>
        ) : closure.check_state === 'rejected' ? (
          <span className="chip cold">Sent back{closure.reject_reason ? `: ${closure.reject_reason}` : ''}</span>
        ) : (
          <span className="chip warm">Not checked yet</span>
        )}
      </div>
    </div>
  );
}

type ModalKind = 'close' | 'move' | 'block' | 'reassign' | 'ask' | 'drop' | 'reopen' | 'decide';

export function TaskPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useTask(id);
  const [modal, setModal] = useState<ModalKind | null>(null);
  const t = q.data;
  return (
    <>
      <Drawer title="Task" onClose={onClose}>
        {q.isLoading ? (
          <Loading />
        ) : q.error ? (
          q.error instanceof ApiFailure && q.error.status === 404 ? (
            <Empty title="This task is not open to you">It may have been dropped, or it belongs to someone else’s client.</Empty>
          ) : (
            <ErrorState error={q.error} onRetry={() => void q.refetch()} />
          )
        ) : t ? (
          <TaskBody task={t} open={setModal} />
        ) : null}
      </Drawer>
      {t && modal === 'close' ? <CloseWithProofModal task={t} onClose={() => setModal(null)} /> : null}
      {t && modal === 'move' ? <MoveDueModal task={t} onClose={() => setModal(null)} /> : null}
      {t && modal === 'block' ? <BlockModal task={t} onClose={() => setModal(null)} /> : null}
      {t && modal === 'reassign' ? <ReassignModal task={t} onClose={() => setModal(null)} /> : null}
      {t && modal === 'ask' ? <AskChangeModal task={t} onClose={() => setModal(null)} /> : null}
      {t && modal === 'drop' ? <DropTaskModal task={t} onClose={() => setModal(null)} onDone={onClose} /> : null}
      {t && modal === 'reopen' ? <ReopenModal task={t} onClose={() => setModal(null)} /> : null}
      {t && modal === 'decide' && t.change_request ? <DecideChangeModal cr={t.change_request} onClose={() => setModal(null)} /> : null}
    </>
  );
}

function TaskBody({ task: t, open }: { task: Task; open: (m: ModalKind) => void }) {
  const { me, can } = useSession();
  const audit = useTaskAudit(t.id, can('audit.read'));
  const today = todayIso();
  const viewOnly = me.acting_as?.mode === 'view';
  const mgr = can('tasks.read.all');
  const mine = t.assignee?.id === me.user.id;
  // Matches the server's canAct: a manager or the assignee. The client's SPOC alone reads the task only (403 on actions).
  const may = !viewOnly && (mgr || mine);
  const finished = t.state === 'closed_unchecked' || t.state === 'closed_checked';
  const live = t.state === 'open' || t.state === 'blocked';
  const cr = t.change_request && t.change_request.status === 'waiting' ? t.change_request : null;
  const due = dueText(t.due, today);

  return (
    <>
      <h2 style={{ fontSize: 18, marginBottom: 10 }}>{t.title}</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <PriorityTaskChip p={t.priority} />
        {t.state === 'dropped' ? <span className="chip">Dropped</span> : <span className={`chip ${t.done ? 'hot' : due.tone}`}>{t.done ? 'Done' : due.label}</span>}
        <SlipChip task={t} />
        {t.blocked ? (
          <span className={`chip ${t.blocked.flagged ? 'cold' : 'warm'}`} title={t.blocked.flagged ? 'Blocked longer than the limit in Settings, so it is flagged to the Ops associate director' : undefined}>
            Blocked {t.blocked.days}d
          </span>
        ) : null}
        {cr ? <span className="chip info">Change requested</span> : null}
        {t.client_waiting ? (
          <span className="chip brand" title="The client is waiting for this">
            Client waiting
          </span>
        ) : null}
      </div>

      <dl className="kv">
        <dt>Client</dt>
        <dd>
          {t.client ? (
            <>
              <Link to={`/clients/book?client=${t.client.id}`} style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}>
                {t.client.name}
              </Link>{' '}
              {t.client.priority === 'high' ? (
                <span className="pm" title="High priority client">
                  ▲
                </span>
              ) : null}
            </>
          ) : (
            'Internal'
          )}
        </dd>
        <dt>SPOC</dt>
        <dd>
          {t.assignee ? t.assignee.name : 'Not assigned'}
          {t.client && t.assignee && t.assignee.id !== t.client.spoc.id ? <span className="sub"> (client SPOC is {firstName(t.client.spoc.name)})</span> : null}
        </dd>
        <dt>Due</dt>
        <dd>
          {dayLabel(t.due)}
          {t.orig_due && t.orig_due !== t.due ? <span className="sub"> first due {dayLabel(t.orig_due)}</span> : null}
        </dd>
        <dt>Came from</dt>
        <dd>
          {TASK_SOURCE_LABEL[t.source]}
          {/* Prototype SRC[t.src] alone (prototype.html:1594). A history import's origin ("Imported from old records",
              no link) only repeats "Imported history", so it is left out; every other origin, or one with a link, stays. */}
          {t.origin_ref && !(t.source === 'history' && t.origin_ref.kind === 'import' && !t.origin_ref.route) ? (
            t.origin_ref.route ? (
              <>
                {', '}
                <Link to={t.origin_ref.route} style={{ color: 'var(--brand)', textDecoration: 'none' }}>
                  {t.origin_ref.label}
                </Link>
              </>
            ) : (
              `, ${t.origin_ref.label}`
            )
          ) : null}
        </dd>
        <dt>Blocked on</dt>
        <dd>{t.blocked ? `${t.blocked.on} (${t.blocked.days} ${t.blocked.days === 1 ? 'day' : 'days'})` : 'Nothing'}</dd>
      </dl>

      {t.slips.length ? (
        <Section label="Due date history">
          <div style={{ fontSize: 13, display: 'grid', gap: 5 }}>
            {t.slips.map((s) => (
              <div key={s.id}>
                {dayLabel(s.from_due)} → <b>{dayLabel(s.to_due)}</b>, {SLIP_REASON_LABEL[s.reason_code]}
                {s.note ? `: ${s.note}` : ''}{' '}
                <span className="sub">
                  ({firstName(s.by.name)}, {dayLabel(s.at.slice(0, 10))})
                </span>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {t.closure ? (
        <Section label="Proof of closure">
          <ProofBlock closure={t.closure} />
        </Section>
      ) : null}

      {cr ? (
        <Section label="Change request">
          <div className="note" style={{ margin: 0 }}>
            <b>{firstName(cr.by.name)}:</b> {CHANGE_REASON_LABEL[cr.reason_code]}
            {cr.suggested_user ? `. Suggests ${cr.suggested_user.name}` : ''}
            {cr.note ? (
              <>
                <br />“{cr.note}”
              </>
            ) : null}
          </div>
        </Section>
      ) : null}

      <Section label="Audit trail">
        <div style={{ fontSize: 13, color: 'var(--sub)', display: 'grid', gap: 5 }}>
          {can('audit.read') ? (
            audit.isLoading ? (
              <div>Loading…</div>
            ) : audit.error ? (
              <div>The audit trail did not load.</div>
            ) : (
              (audit.data ?? []).map((e) => <AuditLine key={e.id} e={e} source={TASK_SOURCE_LABEL[t.source]} />)
            )
          ) : (
            <div>Created from {TASK_SOURCE_LABEL[t.source].toLowerCase()}</div>
          )}
        </div>
      </Section>

      {viewOnly ? (
        <div className="note" style={{ marginTop: 18 }}>
          You are viewing as {me.user.name}. Nothing can be changed.
        </div>
      ) : (
        <div className="split" style={{ marginTop: 22, flexWrap: 'wrap' }}>
          {finished && may ? (
            <button className="btn" onClick={() => open('reopen')}>
              Reopen
            </button>
          ) : null}
          {live && may ? (
            <>
              <button className="btn good" onClick={() => open('close')}>
                Close with proof
              </button>
              <button className="btn" onClick={() => open('move')}>
                Move due date
              </button>
              <button className="btn" onClick={() => open('block')}>
                {t.blocked ? 'Change blocker' : 'Blocked on someone'}
              </button>
            </>
          ) : null}
          {live && can('task.reassign') ? (
            <button className="btn" onClick={() => open('reassign')}>
              Change who does this
            </button>
          ) : null}
          {cr && can('changeRequest.decide') ? (
            <button className="btn pri" onClick={() => open('decide')}>
              Decide on request
            </button>
          ) : null}
          {live && may ? (
            <button className="btn" onClick={() => open('drop')}>
              Drop this task
            </button>
          ) : null}
          {live && mine && !cr && can('changeRequest.raise') ? (
            <button className="btn bad" onClick={() => open('ask')}>
              Ask for a change
            </button>
          ) : null}
        </div>
      )}
      {!viewOnly && live && !can('task.reassign') && mine ? (
        <div className="note" style={{ marginTop: 14 }}>
          Only the founder or the Ops associate director can change who does a task. If someone else is already on it, use Ask for a change.
        </div>
      ) : null}
    </>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <div className="lbl" style={{ margin: '18px 0 8px' }}>
        {label}
      </div>
      {children}
    </>
  );
}

/** One audit row in words, with both people when the change was made while acting as someone. */
function AuditLine({ e, source }: { e: AuditEntry; source: string }) {
  const who = e.actor ? firstName(e.actor.name) : 'The system';
  const as = e.on_behalf_of ? `, acting as ${firstName(e.on_behalf_of.name)}` : '';
  const when = e.at.slice(0, 10);
  const a = (e.after ?? {}) as Record<string, unknown>;
  let what: string;
  if (e.op === 'INSERT') what = `Created from ${source.toLowerCase()}`;
  else if (e.op === 'DELETE') what = 'Removed';
  else {
    const parts: string[] = [];
    for (const c of e.changed) {
      if (c === 'due') parts.push(`due moved to ${dayLabel(String(a.due))}`);
      else if (c === 'assignee_id') parts.push('changed who does this');
      else if (c === 'done') parts.push(a.done ? 'closed with proof' : 'reopened');
      else if (c === 'dropped_at') parts.push(a.dropped_at ? 'dropped' : 'restored');
      else if (c === 'head_priority') parts.push(a.head_priority ? `set to ${HEAD_PRIORITY_LABEL[a.head_priority as 'first']}` : 'order reset');
      else if (c === 'manual_order') parts.push('reordered');
      else if (c === 'priority') parts.push(`urgency set to ${String(a.priority)}`);
      else if (c === 'title') parts.push('renamed');
    }
    what = parts.length ? parts.join(', ').replace(/^./, (s) => s.toUpperCase()) : 'Updated';
  }
  return (
    <div>
      {what} <span className="sub">({who}{as}, {dayLabel(when)})</span>
    </div>
  );
}
